<template>
  <div class="page-body">
    <PageTitle title="Capture Image" />
    <div class="container-fluid">
      <div class="user-profile social-app-profile">
        <div class="row">
          <div class="col-sm-12 box-col-12">
            <div class="card hovercard text-center mb-5"> 
              <video ref="video" playsinline autoplay muted></video>
              <canvas ref="canvas" style="display: none"></canvas> 
              <div class="user-image">
                <div class="avatar">
                  <img :src="photo" alt="Captured photo" class="img-100" v-if="photo">
                </div>
              </div>
              <div v-if="error" class="alert alert-danger">{{ error }}</div>
            </div>
          </div>
          <button @click="capturePhoto" class="btn btn-primary">Capture Photo</button>
          <button @click="switchCamera" class="btn btn-secondary mt-2">Switch Camera</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "@/plugins/axios";
import PageTitle from '@/components/PageTitle.vue'; 

export default {
  components: {
    PageTitle 
  }, 
  data() {
    return {
      photo: null,
      stream: null,
      error: null,
      facingMode: 'user' // Default to front camera
    };
  },
  mounted() {
    this.initWebcam();
  },
  beforeDestroy() {
    this.stopWebcam();
  },
  methods: {
    async initWebcam() {
      this.error = null;
      this.stopWebcam(); // Stop any existing stream
      
      try {
        this.stream = await navigator.mediaDevices.getUserMedia({ 
          video: { 
            facingMode: this.facingMode,
            width: { ideal: 1280 },
            height: { ideal: 720 }
          } 
        });
        
        const video = this.$refs.video;
        video.srcObject = this.stream;
        
        // iOS needs these attributes
        video.setAttribute('playsinline', true);
        video.muted = true;
        
        await video.play().catch(e => {
          this.error = "Could not start video playback: " + e.message;
        });
      } catch (error) {
        console.error("Error accessing webcam:", error);
        this.error = "Could not access the camera. Please ensure you've granted camera permissions.";
        
        // For iOS, we might need to trigger this on user gesture
        if (this.isIOS()) {
          document.addEventListener('click', this.retryInitOnIOS, { once: true });
        }
      }
    },
    
    isIOS() {
      return /iPad|iPhone|iPod/.test(navigator.userAgent) || 
             (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    },
    
    retryInitOnIOS() {
      this.initWebcam();
    },
    
    switchCamera() {
      this.facingMode = this.facingMode === 'user' ? 'environment' : 'user';
      this.initWebcam();
    },
    
    stopWebcam() {
      if (this.stream) {
        this.stream.getTracks().forEach(track => track.stop());
        this.stream = null;
      }
    },
    
    capturePhoto() {
      const video = this.$refs.video;
      const canvas = this.$refs.canvas;
      
      if (!this.stream) {
        this.error = "Camera is not available";
        return;
      }
      
      if (video.readyState < 2) {
        this.error = "Camera is not ready yet. Please wait a moment.";
        return;
      }
      
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      
      const context = canvas.getContext('2d');
      context.drawImage(video, 0, 0, canvas.width, canvas.height);
      
      // iOS sometimes needs a small delay
      setTimeout(() => {
        this.photo = canvas.toDataURL('image/png');
        this.uploadPhoto();
      }, 100);
    },
    
    async uploadPhoto() {
      try {
        const blob = this.dataURLtoBlob(this.photo);
        const formData = new FormData();
        formData.append('image', blob, 'captured_photo.png');
        formData.append('token', localStorage['token']);
        
        const response = await axios.post('setVisitorImage', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
        
        if(response.data.ok === 1) {
          this.stopWebcam();
          this.$router.push({ 
            name: response.data.name ? "visitorpage" : "visitorprofile" 
          });
        }
      } catch (error) {
        console.error('Upload failed:', error);
        this.error = "Failed to upload photo. Please try again.";
      }
    },
    
    dataURLtoBlob(dataURL) {
      const byteString = atob(dataURL.split(',')[1]);
      const mimeString = dataURL.split(',')[0].split(':')[1].split(';')[0];
      const ab = new ArrayBuffer(byteString.length);
      const ia = new Uint8Array(ab);
      for (let i = 0; i < byteString.length; i++) {
        ia[i] = byteString.charCodeAt(i);
      }
      return new Blob([ab], { type: mimeString });
    },
  }
};
</script>

<style scoped>
.hovercard {
  align-items: center;
}

video {
  width: 100%;
  max-width: 250px;
  height: 250px;
  background-color: #000;
  object-fit: cover;
}

.btn {
  margin-top: 15px;
}

.alert {
  margin-top: 15px;
}
</style>